import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Key, UserCheck, UserX } from 'lucide-react';
import { userManagementApi } from '../../../services/api';
import { User, Permission } from '../../../types';
import LoadingCard from '../../../components/LoadingCard';
import Badge from '../../../components/ui/Badge';
import { userStatusVariant } from '../../../utils/badgeVariants';

interface UserDetailsPanelProps {
  userId: number;
  onClose: () => void;
  onUpdate: () => void;
}

const UserDetailsPanel: React.FC<UserDetailsPanelProps> = ({ userId, onClose, onUpdate }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'details' | 'roles' | 'permissions'>('details');

  useEffect(() => {
    const loadUser = async () => {
      try {
        setLoading(true);
        const data = await userManagementApi.getById(userId);
        setUser(data);
      } catch (error) {
        console.error('Failed to load user:', error);
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, [userId]);

  const handleToggleStatus = async () => {
    if (!user) return;
    try {
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      await userManagementApi.updateStatus(userId, newStatus);
      setUser({ ...user, status: newStatus });
      onUpdate();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update user status');
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm('Reset password for this user? A new password will be generated.')) return;
    try {
      const response = await userManagementApi.resetPassword(userId);
      if (response.data?.new_password) {
        alert(`Password reset successfully. New password: ${response.data.new_password}`);
      } else {
        alert('Password reset successfully');
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
      alert('Failed to reset password');
    }
  };

  if (loading) {
    return createPortal(
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
        <div className="max-w-md w-full mx-4">
          <LoadingCard title="User Details" message="Loading user details..." />
        </div>
      </div>,
      document.body
    );
  }

  if (!user) {
    return null;
  }

  // Get all permissions (direct + via roles)
  const allPermissions: Permission[] = [];
  if (user.permissions) {
    allPermissions.push(...user.permissions);
  }
  if (user.roles) {
    user.roles.forEach((role) => {
      if (role.permissions) {
        role.permissions.forEach((perm) => {
          if (!allPermissions.find((p) => p.id === perm.id)) {
            allPermissions.push(perm);
          }
        });
      }
    });
  }

  // Group permissions by module
  const permissionsByModule: Record<string, Permission[]> = {};
  allPermissions.forEach((perm) => {
    const parts = perm.name?.split('.') || [];
    const module = parts[0] || 'other';
    if (!permissionsByModule[module]) {
      permissionsByModule[module] = [];
    }
    permissionsByModule[module].push(perm);
  });

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] pt-20 pb-8" onClick={onClose}>
      <div
        className="theme-bg-primary rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[calc(100vh-8rem)] overflow-y-auto theme-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 theme-bg-primary border-b theme-border px-4 py-2.5 flex items-center justify-between">
          <h2 className="text-lg font-semibold theme-text-primary">User Details</h2>
          <button
            onClick={onClose}
            className="p-1 hover:opacity-80 rounded-full transition-opacity"
          >
            <X className="w-4 h-4 theme-text-secondary" />
          </button>
        </div>

      <div>
        {/* Tabs */}
        <div className="border-b theme-border px-4">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent theme-text-secondary hover:theme-text-primary'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('roles')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'roles'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent theme-text-secondary hover:theme-text-primary'
              }`}
            >
              Roles ({user.roles?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('permissions')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'permissions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent theme-text-secondary hover:theme-text-primary'
              }`}
            >
              Permissions ({allPermissions.length})
            </button>
          </div>
        </div>

        <div className="p-4">
          {activeTab === 'details' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5">Name</label>
                <p className="text-base theme-text-primary">{user.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5">Email</label>
                <p className="text-base theme-text-primary">{user.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5">Status</label>
                <p className="mt-0.5">
                  <Badge variant={userStatusVariant(user.status)}>{user.status || 'active'}</Badge>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5">Created</label>
                <p className="text-base theme-text-primary">
                  {user.created_at ? new Date(user.created_at).toLocaleString() : '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5">Teams</label>
                {user.teams && user.teams.length > 0 ? (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {user.teams.map((team) => (
                      <span
                        key={team.id}
                        className="px-2 py-1 text-xs bg-purple-600 text-white rounded-sm"
                      >
                        {team.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-base theme-text-tertiary">No teams</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="space-y-3">
              {user.roles && user.roles.length > 0 ? (
                user.roles.map((role) => (
                  <div
                    key={role.id}
                    className="p-3 theme-border rounded-lg flex items-center justify-between theme-card"
                  >
                    <div>
                      <div className="font-medium theme-text-primary">{role.name}</div>
                      {role.permissions_count !== undefined && (
                        <div className="text-sm theme-text-secondary">
                          {role.permissions_count} permissions
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="theme-text-tertiary text-center py-8">No roles assigned</p>
              )}
            </div>
          )}

          {activeTab === 'permissions' && (
            <div className="space-y-3">
              {Object.keys(permissionsByModule).length > 0 ? (
                Object.entries(permissionsByModule).map(([module, perms]) => (
                  <div key={module} className="theme-border rounded-lg p-3 theme-card">
                    <h4 className="text-sm font-semibold theme-text-primary mb-2 capitalize">
                      {module.replace('_', ' ')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {perms.map((perm) => {
                        const parts = perm.name?.split('.') || [];
                        const action = parts[1] || 'unknown';
                        return (
                          <span
                            key={perm.id}
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded-sm capitalize"
                          >
                            {action}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : (
                <p className="theme-text-tertiary text-center py-8 text-sm">No permissions</p>
              )}
            </div>
          )}
        </div>

        <div className="border-t theme-border px-4 py-3 flex items-center gap-2">
          <button
            onClick={handleToggleStatus}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition-colors ${
              user.status === 'active'
                ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {user.status === 'active' ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
            {user.status === 'active' ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={handleResetPassword}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors"
          >
            <Key className="w-3.5 h-3.5" />
            Reset Password
          </button>
          <button
            onClick={onClose}
            className="ml-auto px-3 py-1.5 text-sm theme-border rounded theme-bg-secondary theme-text-primary hover:opacity-80"
          >
            Close
          </button>
        </div>
      </div>
      </div>
    </div>,
    document.body
  );
};

export default UserDetailsPanel;

